import type { IToolCallData } from "@/types/data-models";
import { Check, ChevronRight, Loader2, Search, FileSpreadsheet, Database } from "lucide-react";
import { useState } from "react";
import { Streamdown } from "streamdown";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

interface ThinkingTimelineProps {
    toolCalls: IToolCallData[];
    reasoningContent?: string;
    currentToolName?: string | null;
    isStreaming?: boolean;
    defaultExpanded?: boolean;
    formatToolName: (name: string) => string;
}

/** Icon for a specific tool */
function ToolIcon({ name, className }: { name: string; className?: string }) {
    if (name === "find_context_by_query" || name === "rag_tool") {
        return <Search className={cn("h-3.5 w-3.5", className)} />;
    }
    if (name === "excel_sheet_tool") {
        return <FileSpreadsheet className={cn("h-3.5 w-3.5", className)} />;
    }
    return <Database className={cn("h-3.5 w-3.5", className)} />;
}

/** Generate a collapsed summary */
function getCollapsedSummary(
    toolCalls: IToolCallData[],
    formatToolName: (n: string) => string
): string {
    if (toolCalls.length === 0) return "Thought Process";
    if (toolCalls.length === 1) {
        return `Used ${formatToolName(toolCalls[0].name)}`;
    }
    return `Used ${toolCalls.length} tools`;
}

export function ThinkingTimeline({
    toolCalls,
    reasoningContent,
    currentToolName,
    isStreaming = false,
    defaultExpanded = false,
    formatToolName,
}: ThinkingTimelineProps) {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded || isStreaming);
    const [expandedOutputs, setExpandedOutputs] = useState<Set<string>>(new Set());

    // Auto-expand when streaming starts
    const effectiveExpanded = isStreaming ? true : isExpanded;

    const hasContent = toolCalls.length > 0 || !!reasoningContent;
    if (!hasContent) return null;

    const toggleOutput = (toolName: string) => {
        setExpandedOutputs((prev) => {
            const next = new Set(prev);
            if (next.has(toolName)) {
                next.delete(toolName);
            } else {
                next.add(toolName);
            }
            return next;
        });
    };

    return (
        <div className="mb-4">
            {/* Collapsed header — clickable toggle */}
            <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center gap-2 text-xs font-medium text-gray-400 hover:text-gray-200 transition-colors w-full text-left group/toggle select-none"
            >
                <motion.div
                    animate={{ rotate: effectiveExpanded ? 90 : 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className="flex items-center justify-center shrink-0 w-4 h-4 bg-white/10 rounded-sm group-hover/toggle:bg-white/20 transition-colors"
                >
                    <ChevronRight className="h-3 w-3 opacity-70" />
                </motion.div>

                {isStreaming && !effectiveExpanded ? (
                    <span className="flex items-center gap-2">
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                        <span className="text-primary font-medium tracking-wide">
                            {currentToolName
                                ? formatToolName(currentToolName)
                                : "Thinking..."}
                        </span>
                    </span>
                ) : (
                    <span className="opacity-80 group-hover/toggle:opacity-100 transition-opacity tracking-wide text-[13px]">
                        {getCollapsedSummary(toolCalls, formatToolName)}
                    </span>
                )}
            </button>

            {/* Expanded timeline using Framer Motion */}
            <AnimatePresence initial={false}>
                {effectiveExpanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                    >
                        <div className="mt-3 ml-2 pl-4 border-l-[1.5px] border-white/20 space-y-4 py-1">

                            {/* Reasoning / thinking step */}
                            {reasoningContent && (
                                <motion.div
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="space-y-1.5 relative"
                                >
                                    <div className="flex items-center gap-2 text-xs">
                                        {isStreaming && !currentToolName ? (
                                            <div className="absolute -left-[24.5px] bg-[#141414] w-4 h-4 flex items-center justify-center rounded-full">
                                                <Loader2 className="h-3 w-3 animate-spin text-purple-400" />
                                            </div>
                                        ) : (
                                            <span className="absolute -left-[24.5px] bg-[#141414] w-4 h-4 flex items-center justify-center rounded-full">
                                                <span className="h-1.5 w-1.5 rounded-full bg-gray-500/50" />
                                            </span>
                                        )}
                                        <span className="text-gray-400 font-semibold uppercase tracking-wider text-[10px]">Thoughts</span>
                                    </div>
                                    <div className="text-[13px] text-gray-400/80 leading-relaxed max-w-2xl font-mono">
                                        {reasoningContent.substring(0, 300)}
                                        {reasoningContent.length > 300 && "..."}
                                        {isStreaming && !currentToolName && (
                                            <span className="animate-pulse text-purple-400 ml-1">▋</span>
                                        )}
                                    </div>
                                </motion.div>
                            )}

                            {/* Completed tool steps */}
                            {toolCalls.map((tool, index) => (
                                <motion.div
                                    key={tool.name}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    className="space-y-2 relative"
                                >
                                    {/* Tool header */}
                                    <button
                                        type="button"
                                        onClick={() => tool.result && toggleOutput(tool.name)}
                                        className={cn(
                                            "flex items-center gap-2 text-sm w-full text-left group/tool",
                                            tool.result ? "cursor-pointer" : "cursor-default"
                                        )}
                                    >
                                        <span className="absolute -left-[24.5px] bg-[#141414] w-4 h-4 flex items-center justify-center rounded-full">
                                            <Check className="h-3 w-3 text-emerald-500" />
                                        </span>

                                        <div className={cn(
                                            "flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-colors w-fit border border-transparent",
                                            tool.result && "group-hover/tool:bg-white/5 group-hover/tool:border-white/10"
                                        )}>
                                            <ToolIcon name={tool.name} className="text-gray-400" />
                                            <span className="text-white/90 font-medium text-[13px]">
                                                {formatToolName(tool.name)}
                                            </span>
                                            {tool.result && (
                                                <ChevronRight
                                                    className={cn(
                                                        "h-3 w-3 text-gray-500 transition-transform duration-200 ml-1",
                                                        expandedOutputs.has(tool.name) && "rotate-90"
                                                    )}
                                                />
                                            )}
                                        </div>
                                    </button>

                                    {/* Expandable tool output */}
                                    <AnimatePresence>
                                        {expandedOutputs.has(tool.name) && tool.result && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.2, ease: "easeInOut" }}
                                                className="overflow-hidden ml-2"
                                            >
                                                <div className="rounded-lg bg-white/5 border border-white/10 p-3 max-h-64 overflow-y-auto mb-2 mt-1 shadow-sm custom-scrollbar">
                                                    <Streamdown
                                                        parseIncompleteMarkdown={true}
                                                        className="prose prose-xs dark:prose-invert prose-invert max-w-none [&_*]:text-gray-400 [&_pre]:overflow-x-auto [&_pre]:max-w-full [&_code]:break-words [&_p]:break-words text-[12px] font-mono leading-relaxed"
                                                    >
                                                        {tool.result}
                                                    </Streamdown>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            ))}

                            {/* Currently active tool (during streaming) */}
                            {isStreaming &&
                                currentToolName &&
                                !toolCalls.some((t) => t.name === currentToolName) && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="flex items-center gap-2 relative mt-2"
                                    >
                                        <div className="absolute -left-[24.5px] bg-[#141414] w-4 h-4 flex items-center justify-center rounded-full">
                                            <Loader2 className="h-3 w-3 animate-spin text-primary" />
                                        </div>

                                        <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-white/5 border border-white/10 w-fit ml-0 opacity-80">
                                            <ToolIcon name={currentToolName} className="text-primary/70" />
                                            <span className="text-primary/90 font-medium text-[13px]">
                                                {formatToolName(currentToolName)}
                                            </span>
                                        </div>
                                    </motion.div>
                                )}

                            {/* Done marker (when completed) */}
                            {!isStreaming && toolCalls.length > 0 && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.2 }}
                                    className="flex items-center gap-2 relative pt-1"
                                >
                                    <span className="absolute -left-[24.5px] bg-[#141414] w-4 h-4 flex items-center justify-center rounded-full">
                                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500/80" />
                                    </span>
                                    <span className="text-gray-500 text-[11px] font-medium tracking-wide uppercase">Finished</span>
                                </motion.div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
